import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface DescriptionGenerationRequest {
  title: string;
  images?: string[];
  plan_name?: string;
}

export interface DescriptionGenerationResponse {
  success: boolean;
  description: string;
  metadata: {
    characterCount: number;
    wordCount: number;
    planName: string;
    maxLength: number;
  };
}

export interface DescriptionGenerationError {
  success: false;
  message: string;
  errors?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class DescriptionGenerationService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';

  constructor(private http: HttpClient) {}

  generateDescription(
    category: string,
    subcategory: string,
    request: DescriptionGenerationRequest
  ): Observable<DescriptionGenerationResponse> {
    const endpoint = `${this.apiUrl}/${category}/${subcategory}/generate-description`;

    return this.http.post<DescriptionGenerationResponse>(endpoint, request).pipe(
      catchError((error) => {
        console.error('Description generation error:', error);

        let errorMessage = 'Failed to generate description';

        if (error.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment before trying again.';
        } else if (error.status === 503) {
          errorMessage = 'Description generation service is currently unavailable';
        } else if (error.status === 400) {
          errorMessage = error.error?.message || 'Invalid request parameters';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  validateGenerationRequest(request: DescriptionGenerationRequest): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!request.title || request.title.trim().length < 3) {
      errors.push('Title must be at least 3 characters long');
    }

    if (request.images && Array.isArray(request.images)) {
      const invalidImages = request.images.filter(url => !url || typeof url !== 'string');
      if (invalidImages.length > 0) {
        errors.push('All image URLs must be valid strings');
      }
    }

    const validPlans = ['Free', 'Starter', 'Pro', 'Elite'];
    if (request.plan_name && !validPlans.includes(request.plan_name)) {
      errors.push('Invalid plan name specified');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  getSuggestion(
    category: string,
    subcategory: string,
    currentDescription: string,
    title: string,
    planName: string = 'Free'
  ): Observable<{ success: boolean; suggestions: string }> {
    const endpoint = `${this.apiUrl}/${category}/${subcategory}/suggest-description`;

    const request = {
      currentDescription,
      title,
      plan_name: planName
    };

    return this.http.post<{ success: boolean; suggestions: string }>(endpoint, request).pipe(
      catchError((error) => {
        console.error('Description suggestion error:', error);
        return throwError(() => new Error('Failed to get suggestions'));
      })
    );
  }

  isServiceAvailable(): boolean {
    return true;
  }
}
